/**
 * Console watchlist rows (board B6).
 *
 * The legacy account page renders watches as cards in a "Watched products"
 * grid: thumbnail, title, price, Unwatch. It never shows the one field that
 * makes a watch more than a bookmark -- `UserWatch.max_price`, the threshold
 * the server already honours when it decides whether a price move is worth an
 * alert. You could set it once, while starting the watch, and never see it
 * again. These rows show it and let you change it.
 *
 * DOM-free on purpose, like console-cards.js: every function takes data and
 * returns a string, so `test_console_watchlist.py` can run it under Node over
 * a fixture. Binding lives in script.js, next to the fetch.
 *
 * Two things this module will not say. A watch whose listings have all ended
 * is "Ended", never "Sold" -- an expired listing is not a sale, and eBay's API
 * licence forbids the claim either way. And the price on an ended row is the
 * last price we saw it *listed* at, never what it went for.
 */

import { escapeHtml, priceLabel } from './console-cards.js';

/** A number if it is one, else null. Prices arrive as decimal strings. */
function num(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Is this watch currently under the threshold its owner set?
 *
 * The same comparison the server makes in watch_service._classify, so a row
 * that says "below alert" is a row that would have alerted.
 */
export function isBelowAlert(watch) {
  const max = num(watch?.maxPrice ?? watch?.max_price);
  const now = num(watch?.currentPrice);
  return max !== null && now !== null && now <= max;
}

/** No available variant left: every listing under this parent has ended. */
export function hasEnded(watch) {
  return !(watch?.isAvailable ?? watch?.is_available ?? false);
}

/**
 * The four counts in the stat strip, all of them arithmetic over the rows.
 *
 * Nothing here is fetched or estimated -- if a number cannot be computed from
 * the watches themselves it does not appear. `alerts` is how many watches
 * carry a threshold, which is what the header means by "3 alerts".
 */
export function watchStats(watches) {
  const rows = Array.isArray(watches) ? watches : [];
  const stats = { total: rows.length, alerts: 0, below: 0, dropped: 0, rose: 0, ended: 0, movement: 0 };
  for (const w of rows) {
    if (num(w?.maxPrice ?? w?.max_price) !== null) stats.alerts += 1;
    if (hasEnded(w)) {
      stats.ended += 1;
      continue;
    }
    if (isBelowAlert(w)) stats.below += 1;
    const now = num(w?.currentPrice);
    const start = num(w?.priceAtWatch);
    if (now !== null && start !== null) {
      if (now < start) stats.dropped += 1;
      else if (now > start) stats.rose += 1;
      stats.movement += now - start;
    }
  }
  stats.movement = Math.round(stats.movement * 100) / 100;
  return stats;
}

/** "3 Sep" — when the watch started, short enough for a sub-line. */
function shortDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** The signed difference against the price when the watch started. */
function deltaHtml(watch) {
  const now = num(watch?.currentPrice);
  const start = num(watch?.priceAtWatch);
  if (now === null || start === null || now === start) return '';
  const down = now < start;
  const amount = priceLabel(Math.abs(now - start), watch?.currency);
  return `<span class="d ${down ? 'dn' : 'up'}">${down ? '▼' : '▲'} ${escapeHtml(amount)}</span>`;
}

/**
 * One watchlist row.
 *
 * Grid columns follow the board: thumb, title, saved at, now, threshold,
 * action. The ended row keeps all six so the columns line up down the page,
 * and says what it can: a neutral tag and the last price we saw listed.
 */
export function watchRow(watch) {
  if (!watch) return '';
  const ended = hasEnded(watch);
  const below = !ended && isBelowAlert(watch);
  const href = watch.productUrl || '#';
  const name = watch.name || 'Product';
  const threshold = num(watch.maxPrice ?? watch.max_price);
  const classes = ['watch'];
  if (below) classes.push('below');
  if (ended) classes.push('ended');

  const thumb = watch.imageUrl
    ? `<img src="${escapeHtml(watch.imageUrl)}" alt="" loading="lazy" decoding="async">`
    : '<span class="ph" aria-hidden="true"></span>';

  const started = shortDate(watch.createdAt ?? watch.created_at);
  const sub = ended
    ? '<span class="tag">Ended</span>'
    : (started ? `<span class="sub">Watching since ${escapeHtml(started)}</span>` : '');

  const savedAt = num(watch.priceAtWatch);
  const savedCol = savedAt === null
    ? '<div class="c"><span class="k">Saved at</span><span class="v">&mdash;</span></div>'
    : `<div class="c"><span class="k">Saved at</span><span class="v">${escapeHtml(priceLabel(savedAt, watch.currency))}</span></div>`;

  // The ended row's price is the last one we saw it listed at. Not a sale
  // price, and it is labelled so nobody can read it as one.
  let nowCol;
  if (ended) {
    const last = num(watch.lastPrice ?? watch.last_price);
    nowCol = last === null
      ? '<div class="c"><span class="k">Last listed</span><span class="v">&mdash;</span></div>'
      : `<div class="c"><span class="k">Last listed at</span><span class="v">${escapeHtml(priceLabel(last, watch.currency))}</span></div>`;
  } else {
    const now = num(watch.currentPrice);
    nowCol = now === null
      ? '<div class="c"><span class="k">Now</span><span class="v">&mdash;</span></div>'
      : `<div class="c"><span class="k">Now</span><span class="v">${escapeHtml(priceLabel(now, watch.currency))}</span>${deltaHtml(watch)}</div>`;
  }

  // An ended watch keeps its threshold -- the listing can come back, and the
  // server still checks it when one does.
  const thr = `<label class="thr">
      <span class="vh">Alert when it drops under</span>
      <span class="cur">$</span>
      <input type="number" min="0" step="1" inputmode="decimal" placeholder="any drop"
             value="${threshold === null ? '' : escapeHtml(String(threshold))}"
             data-watch-threshold data-parent-id="${escapeHtml(String(watch.masterParentId ?? watch.master_parent_id ?? ''))}">
    </label>`;

  const act = ended
    ? `<a class="btn sm ghost" href="/search.html?q=${encodeURIComponent(name)}">Find similar</a>`
    : `<a class="btn sm${below ? '' : ' ghost'}" href="${escapeHtml(href)}">View</a>`;

  // data-now carries the current price so the threshold input can re-decide
  // the "below your alert" tint without another fetch.
  const nowAttr = num(watch.currentPrice);
  return `<article class="${classes.join(' ')}" data-watch-id="${escapeHtml(String(watch.id ?? ''))}" data-now="${nowAttr === null ? '' : escapeHtml(String(nowAttr))}">
    <a class="th" href="${escapeHtml(href)}" tabindex="-1" aria-hidden="true">${thumb}</a>
    <div class="nm"><a href="${escapeHtml(href)}">${escapeHtml(name)}</a>${sub}</div>
    ${savedCol}
    ${nowCol}
    ${thr}
    <div class="act">${act}<button type="button" class="lnk unwatch-btn" data-watch-id="${escapeHtml(String(watch.id ?? ''))}" data-parent-id="${escapeHtml(String(watch.masterParentId ?? watch.master_parent_id ?? ''))}">Unwatch</button></div>
  </article>`;
}

/** The stat strip above the rows. Only counts that come from the rows. */
export function watchStatsHtml(watches) {
  const s = watchStats(watches);
  const cells = [
    ['Below your alert', s.below],
    ['Dropped since saved', s.dropped],
    ['Rose since saved', s.rose],
    ['Ended', s.ended],
  ];
  return `<div class="strip">${cells.map(
    ([label, value]) => `<div class="stat"><span class="k">${label}</span><span class="v">${value}</span></div>`,
  ).join('')}</div>`;
}

/** Strip plus rows, or the empty state. */
export function watchlistHtml(watches) {
  const rows = Array.isArray(watches) ? watches : [];
  if (!rows.length) {
    return `<div class="panel empty">
      <p>You're not watching anything yet.</p>
      <p class="fnote">Open any listing and tap Watch. Set a price and we'll only tell you when it drops under it.</p>
    </div>`;
  }
  return watchStatsHtml(rows) + `<div class="watches">${rows.map(watchRow).join('')}</div>`;
}
